import { useEffect, useState } from "react";
import { Progress } from "@/components/ui/progress";
import { FileText, CheckCircle, XCircle, X } from "lucide-react";

interface UploadProgressToastProps {
  fileName: string;
  progress: number;
  isUploading: boolean;
  isProcessing?: boolean;
  progressPhase?: 'uploading' | 'processing' | 'complete';
  error: string | null;
  onClose: () => void;
}

export const UploadProgressToast = ({ 
  fileName, 
  progress, 
  isUploading, 
  isProcessing = false,
  progressPhase = 'uploading',
  error, 
  onClose 
}: UploadProgressToastProps) => {
  const [isVisible, setIsVisible] = useState(true);

  // Auto hide after successful upload
  useEffect(() => {
    if (!isUploading && !error && progress === 100) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(onClose, 300); // Allow fade out animation
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isUploading, error, progress, onClose]);

  if (!isVisible) return null;

  const getStatusIcon = () => {
    if (error) return <XCircle className="w-5 h-5 text-red-500" />;
    if (!isUploading && progress === 100) return <CheckCircle className="w-5 h-5 text-green-500" />;
    return <FileText className="w-5 h-5 text-blue-500" />;
  };

  const getStatusText = () => {
    if (error) return "Upload failed";
    if (progressPhase === 'complete' && progress === 100) return "Upload complete - document saved";
    if (progressPhase === 'processing' || isProcessing) return "Processing document - almost done...";
    if (progressPhase === 'uploading' || isUploading) return "Document ready to view - uploading in background...";
    return "Document ready to view - uploading in background...";
  };

  return (
    <div 
      className={`fixed bottom-4 right-4 bg-white border border-slate-200 rounded-lg shadow-lg p-4 min-w-80 max-w-96 z-50 transition-all duration-300 ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
      }`}
    >
      <div className="flex items-start gap-3">
        {getStatusIcon()}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <p className="font-medium text-slate-900 text-sm truncate">
              {fileName}
            </p>
            <button
              onClick={() => {
                setIsVisible(false);
                setTimeout(onClose, 300);
              }}
              className="text-slate-400 hover:text-slate-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-slate-600 mb-2">
            {getStatusText()}
          </p>
          {(isUploading || isProcessing || (!error && progress < 100)) && (
            <div className="space-y-2">
              <div className="space-y-1">
                <Progress value={progress} className="h-2" />
                <div className="flex items-center justify-between">
                  <p className="text-xs text-slate-500">{progress}%</p>
                  <p className="text-xs text-slate-400 capitalize">{progressPhase}</p>
                </div>
              </div>
              {(isUploading || isProcessing) && (
                <p className="text-xs text-amber-600 font-medium">
                  ⚠️ Don't close this window while {isProcessing ? 'processing' : 'uploading'}
                </p>
              )}
            </div>
          )}
          {error && (
            <p className="text-xs text-red-600 mt-1">{error}</p>
          )}
        </div>
      </div>
    </div>
  );
};